import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Car, CalendarClock, PlusCircle, AlertTriangle, DollarSign, TrendingUp,
  CalendarDays, Wallet, Gift, Star, Gauge, Wrench, Bell, ChevronRight, Sparkles, Activity, Wallet2,
  CheckCircle2, CircleDashed, ShieldAlert, PlugZap, Users, HandCoins,
} from "lucide-react";
import { MarkdownLite } from "./MarkdownLite";
import AiInsights from "./AiInsights";
import { toast } from "sonner";
import { fmtTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Sparkline = ({ data, color = "#34d399" }) => {
  const vals = (data || []).map((d) => d.amount || 0);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 1);
  const w = 90, h = 26;
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="mt-2 overflow-visible" data-testid="revenue-sparkline">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {vals.map((v, i) => (
        <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * h).toFixed(1)} r={i === vals.length - 1 ? 2.5 : 0} fill={color} />
      ))}
    </svg>
  );
};

export default function CommandCenter({ token, onAuthError, onNavigate }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [tower, setTower] = useState(null);
  const [fin, setFin] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [crmHealth, setCrmHealth] = useState(null);
  const [maint, setMaint] = useState(null);
  const [resyncingAll, setResyncingAll] = useState(false);
  const [bdPeriod, setBdPeriod] = useState(null);
  const [bd, setBd] = useState(null);
  const [priceAudit, setPriceAudit] = useState(null);
  const [priceAuditLoading, setPriceAuditLoading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const runPricingAudit = async () => {
    setPriceAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/fleet/pricing-audit`, auth);
      setPriceAudit(data);
      toast.success(data.count === 0 ? "Pricing audit clean — no discrepancies found." : `${data.count} booking(s) with pricing discrepancies — report only, nothing changed.`);
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.();
      else toast.error("Could not run the pricing audit.");
    } finally { setPriceAuditLoading(false); }
  };

  const loadCrmHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/system/ghl/crm-health`, { headers: { Authorization: `Bearer ${token}` } });
      setCrmHealth(data);
    } catch { /* non-blocking */ }
    try {
      const { data } = await axios.get(`${API}/fleet/alerts`, { headers: { Authorization: `Bearer ${token}` } });
      setMaint(Array.isArray(data) ? data : data.alerts || []);
    } catch { /* non-blocking */ }
  }, [token]);

  const resyncAllFailed = async () => {
    setResyncingAll(true);
    try {
      const { data } = await axios.post(`${API}/system/ghl/resync-failed`, {}, auth);
      if (data.succeeded > 0) toast.success(`Re-synced ${data.succeeded} of ${data.attempted} booking(s) to GoHighLevel.`);
      if (data.failed > 0) toast.error(`${data.failed} booking(s) still failed to sync — open Bookings to inspect.`);
      if (data.attempted === 0) toast.success("No failed CRM syncs to retry.");
      await loadCrmHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-sync failed bookings.");
    } finally { setResyncingAll(false); }
  };

  const load = useCallback(async () => {
    try {
      const [cc, p, t, f] = await Promise.all([
        axios.get(`${API}/system/command-center`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/system/pulse`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/system/control-tower`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/system/financials`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(cc.data); setPulse(p.data); setTower(t.data); setFin(f.data);
      axios.get(`${API}/system/overdue`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setOverdue(r.data)).catch(() => setOverdue({ items: [] }));
    } catch (e) { if (e.response?.status === 401) onAuthError?.(); }
    finally { setLoading(false); setRefreshing(false); }
  }, [token, onAuthError]);

  const loadBrief = useCallback(async (refresh = false) => {
    setBriefLoading(true);
    try {
      const { data } = await axios.get(`${API}/system/daily-briefing${refresh ? "?refresh=true" : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      setBrief(data);
    } catch { setBrief(null); }
    finally { setBriefLoading(false); }
  }, [token]);

  useEffect(() => { load(); loadBrief(false); loadCrmHealth(); }, [load, loadBrief, loadCrmHealth]);

  // Auto-refresh KPIs so revenue updates right after a payment lands — no reload needed.
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const openBreakdown = async (period) => {
    setBdPeriod(period);
    setBd(null);
    try {
      const { data: d } = await axios.get(`${API}/system/revenue-breakdown?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setBd(d);
    } catch { toast.error("Could not load the revenue breakdown."); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={20} /> Loading mission control…</div>;

  const k = data?.kpis || {};
  const cards = [
    { label: "Cars Rented Now", value: k.active_rentals, sub: `of ${k.total_vehicles || 0} vehicles`, icon: Car, tone: "gold" },
    { label: "Cars Available", value: k.cars_available, icon: CheckCircle2, tone: "green" },
    { label: "Cars in Maintenance", value: k.vehicles_out_of_service, icon: Wrench, tone: k.vehicles_out_of_service ? "amber" : "muted" },
    { label: "Payments Due Today", value: money(k.payments_due_today), sub: `${k.payments_due_today_count || 0} installment(s)`, icon: HandCoins, tone: k.payments_due_today ? "amber" : "muted" },
    { label: "Weekly Special Leads", value: k.ws_leads_week, sub: `${k.ws_leads_today || 0} today · this week`, icon: Sparkles, tone: "gold" },
    { label: "New Club Members", value: k.club_members_week, sub: `${k.club_members_today || 0} today · ${k.club_members_total || 0} total`, icon: Users, tone: "gold" },
    { label: "Referral Bookings", value: k.referral_bookings_total, sub: `${k.referral_bookings_month || 0} this month`, icon: Gift, tone: "muted" },
    { label: "Due Back Today", value: k.vehicles_due_today, icon: CalendarClock, tone: k.vehicles_due_today ? "amber" : "muted" },
    { label: "New Bookings Today", value: k.new_bookings_today, icon: PlusCircle, tone: "muted" },
    { label: "Overdue Rentals", value: k.overdue_rentals, icon: AlertTriangle, tone: k.overdue_rentals ? "red" : "muted" },
    { label: "Revenue Today", value: money(k.revenue_today), icon: DollarSign, tone: "green", breakdown: "today" },
    { label: "Revenue This Week", value: money(k.revenue_week), icon: TrendingUp, tone: "green", spark: true, breakdown: "week" },
    { label: "Revenue This Month", value: money(k.revenue_month), icon: CalendarDays, tone: "green", breakdown: "month" },
    { label: "Payments Pending", value: money(k.payments_pending), sub: `${k.payments_pending_count || 0} booking(s)`, icon: Wallet, tone: k.payments_pending ? "amber" : "muted" },
    { label: "Loyalty / Referrals Issued", value: k.loyalty_rewards_issued, icon: Gift, tone: "muted" },
    { label: "Reviews (Total)", value: k.reviews_total, sub: `${k.reviews_week || 0} this week`, icon: Star, tone: "muted" },
    { label: "Fleet Utilization", value: `${k.fleet_utilization || 0}%`, sub: `${k.active_rentals}/${k.total_vehicles} cars out`, icon: Gauge, tone: "gold" },
    { label: "Out of Service", value: k.vehicles_out_of_service, icon: Wrench, tone: k.vehicles_out_of_service ? "amber" : "muted" },
  ];

  const toneCls = {
    gold: "text-[#F2B705]", green: "text-emerald-400", amber: "text-amber-400", red: "text-red-400", muted: "text-white/70",
  };

  return (
    <div className="space-y-8" data-testid="command-center">
      {/* Fleet Pricing Audit — read-only report */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="fleet-pricing-audit">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-white/85"><HandCoins size={15} className="text-[#F2B705]" /> Fleet Pricing Audit</div>
            <p className="text-[11px] text-white/40 mt-0.5">Scans every active renter for pricing-source mismatches, coverage double-counts, premium-on-standard billing and grandfathered-rate differences. Report only — never changes balances.</p>
          </div>
          <button data-testid="run-pricing-audit-btn" onClick={runPricingAudit} disabled={priceAuditLoading}
            className="bg-[#F2B705] text-black text-xs font-bold px-3 py-1.5 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
            {priceAuditLoading ? <Loader2 className="animate-spin" size={13} /> : <ShieldAlert size={13} />} Run Audit
          </button>
        </div>
        {priceAudit && (
          <div className="mt-3" data-testid="pricing-audit-results">
            {priceAudit.count === 0 ? (
              <p className="text-emerald-300 text-xs flex items-center gap-1.5"><CheckCircle2 size={13} /> No pricing discrepancies across active bookings.</p>
            ) : priceAudit.bookings.map((r, i) => (
              <div key={i} className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 mb-1.5" data-testid={`pricing-audit-row-${i}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/85 font-semibold">{r.name} — {r.vehicle}{r.grandfathered ? <span className="ml-1.5 text-[9px] font-bold text-[#F2B705] border border-[#F2B705]/40 rounded px-1 py-px">🔒 GRANDFATHERED</span> : null}</span>
                  <span className="text-white/50">billed {money(r.observed_week)}/wk · configured {money(r.config_week)}/wk{r.established_week ? ` · locked ${money(r.established_week)}/wk` : ""}</span>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {r.issues.map((iss, j) => <li key={j} className="text-[11px] text-amber-200/85">• {iss}</li>)}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-2xl flex items-center gap-2"><Gauge size={22} className="text-[#F2B705]" /> Operations Command Center</h2>
          <p className="text-white/50 text-sm mt-1">Your live business snapshot. Updated {fmtTime(data?.checked_at)}.</p>
        </div>
        <button data-testid="cc-refresh" onClick={() => { setRefreshing(true); load(); loadCrmHealth(); }} className="flex items-center gap-2 text-sm bg-[#0C0C0C] border border-white/10 px-4 py-2 rounded-md hover:border-[#F2B705]/40 transition-colors">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* MAINTENANCE DUE — impossible to miss */}
      {maint && maint.length > 0 && (() => {
        const od = maint.filter((a) => a.severity === "overdue");
        const now = maint.filter((a) => a.severity === "soon" || a.severity === "ready");
        const soon = maint.filter((a) => a.severity === "watch");
        return (
          <div data-testid="cc-maintenance-due"
            className={`rounded-2xl border p-4 ${od.length ? "border-red-500/70 bg-red-500/[0.08] animate-pulse-slow" : "border-amber-400/40 bg-amber-400/[0.05]"}`}
            style={od.length ? { animation: "ccMaintFlash 1.6s ease-in-out infinite" } : undefined}>
            <style>{`@keyframes ccMaintFlash { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.0);} 50%{box-shadow:0 0 22px 2px rgba(239,68,68,.45);} }`}</style>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <ShieldAlert size={20} className={od.length ? "text-red-400" : "text-amber-300"} />
                <span className="font-display font-black text-base">{od.length ? "🔴 MAINTENANCE OVERDUE" : "MAINTENANCE DUE"}</span>
              </div>
              <div className="flex gap-2 text-[11px] font-black">
                {od.length > 0 && <span data-testid="cc-maint-overdue" className="border border-red-500/60 text-red-300 bg-red-500/10 rounded px-2 py-0.5">OVERDUE: {od.length}</span>}
                {now.length > 0 && <span className="border border-orange-400/60 text-orange-300 bg-orange-400/10 rounded px-2 py-0.5">DUE NOW: {now.length}</span>}
                {soon.length > 0 && <span className="border border-amber-400/50 text-amber-300 bg-amber-400/10 rounded px-2 py-0.5">DUE SOON: {soon.length}</span>}
              </div>
            </div>
            <div className="mt-3 divide-y divide-white/10">
              {[...od, ...now, ...soon].slice(0, 6).map((a, i) => (
                <div key={i} className="py-1.5 flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate"><b className="text-white">{a.vehicle}</b> <span className="text-white/70">— {a.title}</span>
                    <span className="block text-white/45 text-xs truncate">{a.detail}{a.odometer ? ` · odometer ${a.odometer.toLocaleString()} mi` : ""}</span></span>
                  <span className={`text-[10px] font-black shrink-0 ${a.severity === "overdue" ? "text-red-300" : a.severity === "watch" ? "text-amber-300" : "text-orange-300"}`}>
                    {a.severity === "overdue" ? "OVERDUE" : a.severity === "watch" ? "DUE SOON" : "DUE NOW"}</span>
                </div>
              ))}
            </div>
            <button data-testid="cc-maint-open" onClick={() => onNavigate?.("fleet")} className="mt-2 text-xs font-bold text-[#F2B705] underline hover:text-white">Open Fleet Maintenance →</button>
          </div>
        );
      })()}

      {/* CRM Health Banner */}
      {crmHealth && (
        <div
          data-testid="cc-crm-health"
          className={`rounded-2xl border p-4 ${crmHealth.failed > 0 ? "border-red-500/40 bg-red-500/[0.07]" : "border-white/10 bg-[#0C0C0C]"}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {crmHealth.failed > 0
                ? <ShieldAlert size={20} className="text-red-400 shrink-0" />
                : <CheckCircle2 size={20} className="text-emerald-400 shrink-0" />}
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-white/90" data-testid="cc-crm-health-title">
                  {crmHealth.failed > 0
                    ? `${crmHealth.failed} booking${crmHealth.failed === 1 ? "" : "s"} failed to sync to GoHighLevel`
                    : "GoHighLevel CRM is healthy"}
                </div>
                <div className="text-white/45 text-xs mt-0.5">CRM opportunity sync status across active bookings</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => onNavigate?.("bookings")} data-testid="cc-crm-synced"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.08] hover:brightness-125 transition-all">
                <CheckCircle2 size={13} /> Synced {crmHealth.synced}
              </button>
              <button onClick={() => onNavigate?.("bookings")} data-testid="cc-crm-not-synced"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/10 text-white/50 bg-white/[0.03] hover:brightness-125 transition-all">
                <CircleDashed size={13} /> Not Synced {crmHealth.not_synced}
              </button>
              <button onClick={() => onNavigate?.("bookings")} data-testid="cc-crm-failed"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all hover:brightness-125 ${crmHealth.failed > 0 ? "border-red-500/40 text-red-300 bg-red-500/[0.1]" : "border-white/10 text-white/40 bg-white/[0.03]"}`}>
                <ShieldAlert size={13} /> Failed {crmHealth.failed}
              </button>
              {crmHealth.failed > 0 && (
                <button onClick={resyncAllFailed} disabled={resyncingAll} data-testid="cc-crm-resync-all"
                  className="inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full bg-[#F2B705] text-black hover:bg-white transition-colors disabled:opacity-60">
                  <RefreshCw size={13} className={resyncingAll ? "animate-spin" : ""} /> Re-sync all
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Business Pulse + Daily Briefing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {pulse && (
          <div data-testid="cc-pulse" className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
            <span className="text-white/45 text-xs uppercase tracking-widest flex items-center gap-1.5"><Activity size={13} /> Business Pulse</span>
            <div className="relative my-3">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#ffffff14" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke={pulse.score >= 70 ? "#34d399" : pulse.score >= 50 ? "#F2B705" : "#f87171"} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${(pulse.score / 100) * 326.7} 326.7`} transform="rotate(-90 60 60)" />
                <text x="60" y="58" textAnchor="middle" className="fill-white font-black" style={{ fontSize: 30 }}>{pulse.score}</text>
                <text x="60" y="78" textAnchor="middle" className="fill-white/40" style={{ fontSize: 10 }}>/ 100</text>
              </svg>
            </div>
            <span className={`font-display font-bold ${pulse.score >= 70 ? "text-emerald-400" : pulse.score >= 50 ? "text-[#F2B705]" : "text-red-400"}`}>{pulse.label}</span>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-4 w-full text-left">
              {pulse.subscores.map((s) => (
                <div key={s.label} className="text-xs">
                  <div className="flex justify-between text-white/55"><span className="truncate">{s.label}</span><span className="text-white/80 font-semibold">{s.value}%</span></div>
                  <div className="h-1 bg-white/10 rounded mt-0.5"><div className="h-1 rounded bg-[#F2B705]" style={{ width: `${s.value}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div data-testid="cc-briefing" className="bg-gradient-to-br from-[#15120a] to-[#0C0C0C] border border-[#F2B705]/25 rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-black text-lg flex items-center gap-2"><Sparkles size={18} className="text-[#F2B705]" /> AI Daily Briefing</h3>
            <button data-testid="cc-briefing-refresh" onClick={() => loadBrief(true)} disabled={briefLoading} className="text-xs flex items-center gap-1.5 text-white/50 hover:text-[#F2B705] transition-colors disabled:opacity-50">
              <RefreshCw size={13} className={briefLoading ? "animate-spin" : ""} /> Regenerate
            </button>
          </div>
          {briefLoading && <div className="flex items-center gap-2 text-white/50 py-6"><Loader2 className="animate-spin" size={16} /> Generating today's briefing…</div>}
          {!briefLoading && brief?.briefing && <MarkdownLite text={brief.briefing} className="mt-3 text-sm text-white/85 leading-relaxed" />}
          {!briefLoading && !brief?.briefing && <p className="text-white/40 text-sm mt-3">Briefing unavailable right now.</p>}
        </div>
      </div>

      {/* AI Business Assistant — actionable insights */}
      <AiInsights token={token} />

      {/* Control Tower */}
      {tower && (
        <div data-testid="cc-control-tower" className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5">
          <h3 className="font-display font-black text-lg flex items-center gap-2">
            🗼 Control Tower
            <span className="text-white/30 text-sm font-normal">— what needs attention first</span>
          </h3>
          <div className="flex gap-2 mt-2 text-xs flex-wrap">
            <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-400/30">{tower.counts.high} urgent</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-400/30">{tower.counts.medium} soon</span>
            <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-white/50 border border-white/15">{tower.counts.low} info</span>
            {tower.counts.waiting_docs > 0 && (
              <span data-testid="cc-waiting-docs" className="px-2 py-0.5 rounded-full bg-[#F2B705]/15 text-[#F2B705] border border-[#F2B705]/30">
                {tower.counts.waiting_docs} waiting on docs
              </span>
            )}
          </div>
          <div className="mt-4 space-y-2">
            {tower.items.length === 0 && <p className="text-white/40 text-sm">Nothing needs attention — you're all caught up. 🎉</p>}
            {tower.items.map((it, i) => (
              <button key={i} data-testid={`tower-item-${i}`} onClick={() => onNavigate?.(it.category)}
                className="w-full text-left flex items-center justify-between gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 hover:border-[#F2B705]/40 transition-colors">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${it.severity === "high" ? "bg-red-400" : it.severity === "medium" ? "bg-amber-400" : "bg-white/40"}`} />
                  <span className="min-w-0">
                    <span className="text-sm text-white/85 block truncate">{it.title}</span>
                    <span className="text-xs text-[#F2B705]/80">→ {it.action}</span>
                  </span>
                </span>
                <ChevronRight size={16} className="text-white/30 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Overdue Rentals & Late Fees */}
      {overdue?.items?.length > 0 && (
        <div data-testid="cc-overdue" className="bg-[#0C0C0C] border border-red-500/30 rounded-2xl p-5">
          <h3 className="font-display font-black text-lg flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" /> Overdue Rentals &amp; Late Fees
            <span className="text-white/30 text-sm font-normal">
              {overdue.items.every((o) => !o.escalation_enabled) ? "— auto-escalation is off; review manually" : "— the system is chasing these automatically"}
            </span>
          </h3>
          <div className="mt-4 space-y-2">
            {overdue.items.map((o) => {
              const escalationOff = !o.escalation_enabled;
              const stageCls = { repossession: "bg-red-500/15 text-red-400 border-red-400/30", recovery: "bg-red-500/15 text-red-400 border-red-400/30", call: "bg-amber-500/15 text-amber-400 border-amber-400/30", notified: "bg-amber-500/15 text-amber-400 border-amber-400/30", reminded: "bg-white/[0.06] text-white/60 border-white/15" }[o.stage] || "bg-white/[0.06] text-white/60 border-white/15";
              const stageLabel = { repossession: "Repossession (24h+)", recovery: "Recovery (6h+)", call: "Owner Call (3h+)", notified: "Notified (1h+)", reminded: "Reminded (30m+)" }[o.stage] || o.stage;
              const steps = [["30m", "sms_30"], ["1h", "sms_email_60"], ["3h", "task_180"], ["6h", "recovery_360"], ["24h", "repo_1440"]];
              return (
                <div key={o.booking_id} data-testid={`overdue-${o.booking_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-white/90 text-sm font-semibold">{o.name}</span>
                      <span className="text-white/40 text-sm"> · {o.vehicle}</span>
                      {escalationOff ? (
                        <span data-testid={`overdue-escalation-off-${o.booking_id}`} className="ml-2 text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/40 bg-white/[0.04]">Auto-escalation off</span>
                      ) : (
                        <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full border ${stageCls}`}>{stageLabel}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-white/50">{o.hours_late}h late</span>
                      {o.fee_accrual_enabled ? (
                        <span className="text-red-400 font-bold">Late fee {money(o.late_fee)}</span>
                      ) : (
                        <span data-testid={`overdue-fee-off-${o.booking_id}`} className="text-white/35 italic">Late fee charging off</span>
                      )}
                      <span className="text-white/70">Balance {money(o.balance)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2.5">
                    {steps.map(([lbl, key]) => {
                      const done = !!(o.escalations && o.escalations[key]);
                      return (
                        <span key={key} className={`text-[10px] px-2 py-0.5 rounded-full border ${done ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/10" : "border-white/10 text-white/30"}`}>
                          {done ? "✓ " : ""}{lbl}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Financial snapshot */}
      {fin && (
        <div data-testid="cc-financials" className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5">
          <h3 className="font-display font-black text-lg flex items-center gap-2"><Wallet2 size={18} className="text-emerald-400" /> Financial Dashboard</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {[
              { l: "Revenue (Month)", v: money(fin.revenue_month), t: "text-emerald-400" },
              { l: "Collected (All-time)", v: money(fin.collected_all), t: "text-emerald-400" },
              { l: "Outstanding", v: money(fin.outstanding), t: fin.outstanding ? "text-amber-400" : "text-white/70" },
              { l: "Net (All-time)", v: money(fin.net_all), t: fin.net_all >= 0 ? "text-emerald-400" : "text-red-400" },
            ].map((c) => (
              <div key={c.l} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
                <div className="text-white/45 text-[11px] uppercase tracking-wide">{c.l}</div>
                <div className={`text-2xl font-display font-black mt-1 ${c.t}`}>{c.v}</div>
              </div>
            ))}
          </div>
          {fin.top_vehicles?.some((v) => v.revenue > 0) && (
            <div className="mt-4">
              <div className="text-white/40 text-xs uppercase tracking-wide mb-2">Top Earning Vehicles</div>
              <div className="space-y-1.5">
                {fin.top_vehicles.filter((v) => v.revenue > 0).map((v) => (
                  <div key={v.vehicle} className="flex items-center justify-between text-sm bg-[#0A0A0A] border border-white/10 rounded px-3 py-1.5">
                    <span className="text-white/80">{v.vehicle}</span><span className="text-emerald-400 font-semibold">{money(v.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const Tag = c.breakdown ? "button" : "div";
          return (
            <Tag key={c.label} data-testid={`cc-kpi-${c.label}`}
              onClick={c.breakdown ? () => openBreakdown(c.breakdown) : undefined}
              className={`bg-[#0C0C0C] border border-white/10 rounded-xl p-4 hover:border-[#F2B705]/30 transition-colors ${c.breakdown ? "text-left cursor-pointer" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-white/45 text-xs uppercase tracking-wider">{c.label}</span>
                <Icon size={16} className={toneCls[c.tone]} />
              </div>
              <div className={`text-3xl font-display font-black mt-2 ${toneCls[c.tone]}`}>{c.value ?? 0}</div>
              {c.sub && <div className="text-white/35 text-xs mt-1">{c.sub}</div>}
              {c.breakdown && <div className="text-white/25 text-[10px] mt-1">Click for payment breakdown</div>}
              {c.spark && <Sparkline data={data?.revenue_7d} />}
            </Tag>
          );
        })}
      </div>

      {bdPeriod && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBdPeriod(null)}>
          <div data-testid="revenue-breakdown-dialog" className="bg-[#0C0C0C] border border-white/15 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-black text-lg flex items-center gap-2">
                <DollarSign size={18} className="text-emerald-400" />
                Revenue {bdPeriod === "today" ? "Today" : bdPeriod === "week" ? "This Week" : "This Month"}
              </h3>
              <button data-testid="revenue-breakdown-close" onClick={() => setBdPeriod(null)} className="text-white/50 hover:text-white text-2xl leading-none">×</button>
            </div>
            {!bd && <div className="flex items-center gap-2 text-white/50 py-8"><Loader2 className="animate-spin" size={16} /> Loading payments…</div>}
            {bd && (
              <>
                <p className="text-white/45 text-xs mb-4">{bd.start} → {bd.end} (Central Time) · {bd.count} payment{bd.count !== 1 ? "s" : ""} · Total <span className="text-emerald-400 font-bold">{money(bd.total)}</span></p>
                {bd.payments.length === 0 && <p className="text-white/40 text-sm py-6 text-center">No payments received in this period yet.</p>}
                {bd.payments.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
                        <th className="py-2 pr-3">Customer</th>
                        <th className="py-2 pr-3">Booking</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                        <th className="py-2 pr-3">Type</th>
                        <th className="py-2 pr-3">Received (CT)</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 text-right">Refund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bd.payments.map((p) => (
                        <tr key={p.id} data-testid={`revenue-payment-${p.id}`} className="border-b border-white/5">
                          <td className="py-2 pr-3 text-white font-semibold">{p.customer}</td>
                          <td className="py-2 pr-3 text-white/55 text-xs">{p.vehicle}</td>
                          <td className={`py-2 pr-3 text-right font-bold ${p.net < 0 ? "text-red-300" : "text-emerald-400"}`}>{money(p.net)}</td>
                          <td className="py-2 pr-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-white/15 text-white/60">{(p.type || "").replace(/_/g, " ")}</span></td>
                          <td className="py-2 pr-3 text-white/55 text-xs whitespace-nowrap">{p.paid_at_ct}</td>
                          <td className="py-2 pr-3 text-emerald-300/80 text-xs">{p.status}</td>
                          <td className="py-2 text-right text-white/45 text-xs">{p.refunded > 0 ? money(p.refunded) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="cc-action-items">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Bell size={18} className="text-[#F2B705]" /> Action Items <span className="text-white/30 text-sm font-normal">({(data?.action_items || []).length})</span></h3>
          <div className="mt-4 space-y-2">
            {(data?.action_items || []).length === 0 && <p className="text-white/40 text-sm">All clear — nothing needs attention right now. 🎉</p>}
            {(data?.action_items || []).map((a, i) => (
              <button key={i} data-testid={`cc-action-${i}`} onClick={() => onNavigate?.(a.type === "reviews" ? "reviews" : a.type === "payment" ? "money" : "bookings")}
                className="w-full text-left flex items-center justify-between gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 hover:border-[#F2B705]/40 transition-colors">
                <span className="text-sm text-white/80 flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${a.type === "overdue" ? "bg-red-400" : a.type === "due" ? "bg-amber-400" : "bg-[#F2B705]"}`} />
                  {a.label}
                </span>
                <ChevronRight size={15} className="text-white/30 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="cc-out-of-service">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Wrench size={18} className="text-amber-400" /> Out of Service</h3>
          <div className="mt-4 space-y-2">
            {(data?.out_of_service || []).length === 0 && <p className="text-white/40 text-sm">Every vehicle is in service. 🚗</p>}
            {(data?.out_of_service || []).map((v, i) => (
              <div key={i} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-sm">
                <span className="text-white/80">{v.vehicle}</span>
                <span className="text-amber-400 text-xs uppercase tracking-wide">{v.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
